<script setup>
defineProps({
  suggestions: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['applyAction']);

const handleApplyAction = action => {
  emit('applyAction', action);
};

const getActionIcon = type => {
  const icons = {
    add_label: 'i-lucide-tag',
    assign_team: 'i-lucide-users',
    apply_macro: 'i-lucide-zap',
    resolve: 'i-lucide-check-circle',
    snooze: 'i-lucide-clock',
    escalate: 'i-lucide-arrow-up-circle',
  };
  return icons[type] || 'i-lucide-play';
};
</script>

<template>
  <div class="rounded-lg bg-n-solid-2 p-4">
    <h4
      class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider mb-3"
    >
      {{ $t('AGENTS_SIDECAR.SUGGESTIONS.TITLE') }}
    </h4>

    <!-- Tags -->
    <div v-if="suggestions.tags?.length" class="mb-4">
      <p class="text-xs text-n-slate-10 mb-2">
        {{ $t('AGENTS_SIDECAR.SUGGESTIONS.TAGS') }}
      </p>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="tag in suggestions.tags"
          :key="tag.name"
          class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-n-alpha-3 text-n-slate-12 hover:bg-n-alpha-4 transition-colors"
          @click="handleApplyAction({ type: 'add_label', name: tag.name })"
        >
          <span class="i-lucide-tag text-xs" />
          {{ tag.name }}
          <span class="i-lucide-plus text-xs opacity-50" />
        </button>
      </div>
    </div>

    <!-- Actions -->
    <div v-if="suggestions.actions?.length" class="mb-4">
      <p class="text-xs text-n-slate-10 mb-2">
        {{ $t('AGENTS_SIDECAR.SUGGESTIONS.ACTIONS') }}
      </p>
      <div class="space-y-2">
        <button
          v-for="action in suggestions.actions"
          :key="action.name"
          class="w-full flex items-center gap-3 p-2.5 rounded-lg bg-n-solid-1 border border-n-weak hover:bg-n-solid-3 transition-colors text-left"
          @click="handleApplyAction(action)"
        >
          <span
            :class="getActionIcon(action.type)"
            class="text-n-slate-10 text-lg"
          />
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ action.name }}
            </p>
            <p
              v-if="action.description"
              class="text-xs text-n-slate-10 truncate"
            >
              {{ action.description }}
            </p>
          </div>
          <span v-if="action.confidence" class="text-xs text-n-slate-10">
            {{ Math.round(action.confidence * 100) }}%
          </span>
        </button>
      </div>
    </div>

    <!-- Macros -->
    <div v-if="suggestions.macros?.length">
      <p class="text-xs text-n-slate-10 mb-2">
        {{ $t('AGENTS_SIDECAR.SUGGESTIONS.MACROS') }}
      </p>
      <div class="space-y-2">
        <button
          v-for="macro in suggestions.macros"
          :key="macro.name"
          class="w-full flex items-center gap-3 p-2.5 rounded-lg bg-n-solid-1 border border-n-weak hover:bg-n-solid-3 transition-colors text-left"
          @click="handleApplyAction({ type: 'apply_macro', ...macro })"
        >
          <span class="i-lucide-zap text-n-slate-10 text-lg" />
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ macro.name }}
            </p>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>
